import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import monitorApi from "../../api/monitorApi";
import Header from "../../components/Header/Header";
import ShutdownAddNav from "../../components/ShutdownAddNav/ShutdownAddNav";
import { buildShutdownTablePayload } from "./buildShutdownTablePayload";
import {
  ExistingShutdownType,
  RegionOutageType,
  ResultMessageType,
  ShutdownTypeType,
} from "./ShutdownAddPageTypes";
import ShutdownLine from "./ShutdownLine/ShutdownLine";

const EMPTY_ARRAY: any[] = [];

function ShutdownAddPage() {
  const [amountOfSendedShutdowns, setAmountOfSendedShutdowns] =
    useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const submitLock = useRef(false);
  const { t } = useTranslation();
  const [resultMessage, setResultMessage] = useState<ResultMessageType>({
    text: null,
    error: false,
  });

  const [dateOfOutage, setDateOfOutage] = useState<string>("1970-01-01");
  const [regions, setRegions] = useState<Array<{ id: number; name: string }>>([
    { id: 0, name: "" },
  ]);
  const [regionOutages, setRegionOutage] = useState<Array<RegionOutageType>>(
    [],
  );
  const [outageTypes, setOutageTypes] = useState<Array<ShutdownTypeType>>([]);
  const [existingShutdowns, setExistingShutdowns] = useState<
    Array<ExistingShutdownType>
  >([]);
  const [userHasInteracted, setUserHasInteracted] = useState<boolean>(false);
  const [shutdownTypeColors, setShutdownTypeColors] = useState<
    Record<number, string>
  >({});

  const fetchShutdownTypeColors = useCallback(async () => {
    try {
      const response = await monitorApi.getStats();

      const { sd_type } = response.data;

      if (sd_type && Array.isArray(sd_type)) {
        const colorMap: Record<number, string> = {};
        sd_type.forEach((type: { id: number; color: string }) => {
          colorMap[type.id] = type.color || "#AACCAA"; // fallback to default color
        });
        setShutdownTypeColors(colorMap);
      }
    } catch (error) {
      console.info("Error fetching shutdown type colors:", error);
    }
  }, []);

  const fetchShutdownData = useCallback(async (date: string) => {
    if (!date) {
      setRegionOutage([]);
      return;
    }
    if (date === "1970-01-01") {
      setRegionOutage([]);
      return;
    }

    try {
      const response = await monitorApi.getShutdownTable(date);

      const { regions: regionsData, outage_types, shutdowns } = response.data;

      if (regionsData) setRegions(regionsData);
      if (outage_types) {
        const sorted = [...outage_types].sort(
          (a: ShutdownTypeType, b: ShutdownTypeType) => {
            const co = (a.column_order ?? 0) - (b.column_order ?? 0);
            if (co !== 0) {
              return co;
            }
            const mp = (v: number | null | undefined) =>
              v == null ? Number.POSITIVE_INFINITY : v;
            const mo = mp(a.map_priority) - mp(b.map_priority);
            if (mo !== 0) {
              return mo;
            }
            return a.id - b.id;
          },
        );
        setOutageTypes(sorted);
      }
      if (shutdowns) {
        setExistingShutdowns(shutdowns);

        // Update regionOutages based on existing shutdowns
        const existingRegionOutages: Record<number, number[]> = {};
        shutdowns.forEach((shutdown) => {
          shutdown.regions.forEach((region) => {
            if (!existingRegionOutages[region.id]) {
              existingRegionOutages[region.id] = [];
            }
            if (
              !existingRegionOutages[region.id].includes(
                shutdown.shutdown_type.id,
              )
            ) {
              existingRegionOutages[region.id].push(shutdown.shutdown_type.id);
            }
          });
        });

        const regionOutagesArray = Object.entries(existingRegionOutages).map(
          ([regionId, outageTypes]) => ({
            region_id: parseInt(regionId),
            outages_types: outageTypes,
          }),
        );

        setRegionOutage(regionOutagesArray);
      }
      setUserHasInteracted(false);
    } catch (error) {
      console.info(
        "There was an error fetching shutdown data for date:",
        date,
        error,
      );
      setExistingShutdowns([]);
    }
  }, []);

  const handleReset = useCallback(() => {
    // Reset to today's date
    const todaysDate = get_date_now_iso();
    setDateOfOutage(todaysDate);

    // Clear form state
    setRegionOutage([]);
    setUserHasInteracted(false);
    clearError();

    // Explicitly fetch data for reset
    fetchShutdownData(todaysDate);
  }, [fetchShutdownData]);

  const hasChanges = useCallback(() => {
    if (!userHasInteracted) {
      return false;
    }

    // Build a map of existing region shutdowns for easier comparison
    const existingMap: Record<number, Set<number>> = {};
    existingShutdowns.forEach((shutdown) => {
      shutdown.regions.forEach((region) => {
        if (!existingMap[region.id]) {
          existingMap[region.id] = new Set();
        }
        existingMap[region.id].add(shutdown.shutdown_type.id);
      });
    });

    // Build a map of current region outages
    const currentMap: Record<number, Set<number>> = {};
    regionOutages.forEach((outage) => {
      currentMap[outage.region_id] = new Set(outage.outages_types);
    });

    // Compare the two maps
    const existingRegions = Object.keys(existingMap).map(Number);
    const currentRegions = Object.keys(currentMap).map(Number);

    // Check if different number of regions
    if (existingRegions.length !== currentRegions.length) {
      return true;
    }

    // Check each region
    for (const regionId of existingRegions) {
      const currentTypes = currentMap[regionId];

      // If region doesn't exist in current selection
      if (!currentTypes) {
        return true;
      }

      const existingTypes = existingMap[regionId];

      // Check if different number of outage types for this region
      if (existingTypes.size !== currentTypes.size) {
        return true;
      }

      // Check each outage type
      for (const typeId of existingTypes) {
        if (!currentTypes.has(typeId)) {
          return true;
        }
      }
    }

    return false;
  }, [userHasInteracted, regionOutages, existingShutdowns]);

  const handleCheckboxChanged = useCallback(
    (region_id: number, outages_types: number[]) => {
      setUserHasInteracted(true);

      setRegionOutage((prev) => {
        let new_data = [...prev];

        const existingIndex = new_data.findIndex(
          (item) => item.region_id === region_id,
        );
        const isEmpty = outages_types.length === 0;

        if (isEmpty) {
          new_data = new_data.filter((item) => item.region_id !== region_id);
        } else {
          if (existingIndex !== -1) {
            new_data[existingIndex] = {
              region_id: region_id,
              outages_types: outages_types,
            };
          } else {
            new_data.push({
              region_id: region_id,
              outages_types: outages_types,
            });
          }
        }

        return new_data;
      });
    },
    [],
  );
  const handleColumnToggle = useCallback(
    (outageTypeId: number, selectAll: boolean) => {
      setUserHasInteracted(true);

      if (selectAll) {
        // Select all regions for this outage type
        setRegionOutage((prev) => {
          const newData = [...prev];

          regions.forEach((region) => {
            const existingIndex = newData.findIndex(
              (item) => item.region_id === region.id,
            );

            if (existingIndex !== -1) {
              // Add the outage type if not already present
              if (
                !newData[existingIndex].outages_types.includes(outageTypeId)
              ) {
                newData[existingIndex] = {
                  ...newData[existingIndex],
                  outages_types: [
                    ...newData[existingIndex].outages_types,
                    outageTypeId,
                  ],
                };
              }
            } else {
              // Create new entry for this region
              newData.push({
                region_id: region.id,
                outages_types: [outageTypeId],
              });
            }
          });

          return newData;
        });
      } else {
        // Deselect all regions for this outage type
        setRegionOutage((prev) => {
          const result = prev
            .map((item) => ({
              ...item,
              outages_types: item.outages_types.filter(
                (typeId) => typeId !== outageTypeId,
              ),
            }))
            .filter((item) => item.outages_types.length > 0);
          return result;
        });
      }
    },
    [regions],
  );

  useEffect(() => {
    fetchShutdownData(dateOfOutage);
  }, [dateOfOutage, fetchShutdownData]);

  useEffect(() => {
    setDateOfOutage(get_date_now_iso());
    fetchShutdownTypeColors();
  }, [fetchShutdownTypeColors]);

  const clearError = () => {
    setResultMessage({
      text: null,
      error: false,
    });
  };

  const get_date_now_iso = () => {
    const date_now = new Date();
    const warsawDate = new Date(
      date_now.toLocaleString("en-US", { timeZone: "Europe/Warsaw" }),
    );

    const year = warsawDate.getFullYear();
    const month = String(warsawDate.getMonth() + 1).padStart(2, "0");
    const day = String(warsawDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const sendData = () => {
    if (submitLock.current) {
      return;
    }
    if (dateOfOutage) {
      const data_to_send = buildShutdownTablePayload(regionOutages);

      submitLock.current = true;
      setIsSubmitting(true);
      monitorApi
        .addShutdownTable({
          date: dateOfOutage,
          region_outages: data_to_send,
        })
        .then(() => {
          setResultMessage({
            error: false,
            text: t("shutdown_add_page.success_message"),
          });
          setAmountOfSendedShutdowns(amountOfSendedShutdowns + 1);
          fetchShutdownData(dateOfOutage);
        })
        .catch((error) => {
          setResultMessage({
            error: true,
            text: t("shutdown_add_page.error_message") + error.message,
          });
          console.info(error);
        })
        .finally(() => {
          submitLock.current = false;
          setIsSubmitting(false);
        });
    } else {
      setResultMessage({
        error: true,
        text: t("shutdown_add_page.error_no_date"),
      });
    }
  };
  return (
    <main className="w-full">
      <Header />
      <ShutdownAddNav />

      <div
        data-testid="shutdown-add-page"
        className="max-w-[62.5rem] mx-auto p-[1rem] md:p-[2rem]"
      >
        <div className="my-2 flex flex-col md:flex-row md:justify-between gap-3">
          <div className="flex gap-1 order-1 md:order-1">
            <input
              className="p-2 bg-slate-100"
              type="date"
              value={dateOfOutage}
              onChange={(e) => {
                setDateOfOutage(e.target.value);
                clearError();
              }}
              data-testid="shutdowns-date-input"
            />
            <button
              onClick={handleReset}
              className="bg-blue-300 hover:bg-blue-400 rounded-md px-3 py-2 text-sm"
              title="Reset and reload data"
            >
              Reset
            </button>
          </div>

          <div
            className={
              !resultMessage.text
                ? "hidden"
                : !resultMessage.error
                  ? "bg-green-300 w-full flex rounded-md p-3 mx-0 md:mx-3 order-3 md:order-2"
                  : "bg-red-300 w-full flex rounded-md p-3 mx-0 md:mx-3 order-3 md:order-2"
            }
          >
            <p className="mr-3">{resultMessage.text}</p>
          </div>

          <button
            onClick={sendData}
            disabled={!hasChanges() || isSubmitting}
            title="Add shutdowns"
            data-testid="add-shutdowns-button"
            className={
              hasChanges() && !isSubmitting
                ? "bg-blue-500 hover:bg-blue-600 text-white rounded-md px-3 py-2 order-2 md:order-3"
                : "bg-gray-200 text-gray-500 rounded-md px-3 py-2 cursor-not-allowed order-2 md:order-3"
            }
          >
            {t("shutdown_add_page.add_button_text")}
          </button>
        </div>
        <table className="w-full border-separate border-spacing-0 border border-gray-400 mb-16">
          <thead className="sticky top-0 z-50">
            <tr className="w-max bg-white">
              <th className="w-auto py-2 border border-gray-300 bg-white">
                {t("shutdown_add_page.region")}
              </th>
              {outageTypes.map((shutdown_type) => {
                if (shutdown_type.display_type !== 0) {
                  return (
                    <th
                      className="relative w-auto py-2 border border-gray-300 bg-white"
                      key={shutdown_type.id}
                    >
                      {shutdown_type.name}
                      {shutdown_type.count_on_graph ? (
                        <span className="group absolute top-1 right-1 z-10">
                          <span
                            className="block h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: "#723509", opacity: 0.5 }}
                            aria-label="Count on graph"
                            data-testid={`count-on-graph-${shutdown_type.id}`}
                          />
                          <span
                            role="tooltip"
                            className="pointer-events-none absolute right-0 top-full z-50 mt-0.5 hidden whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-normal leading-tight text-white shadow group-hover:block"
                            style={{ backgroundColor: "#723509" }}
                          >
                            Count on graph
                          </span>
                        </span>
                      ) : null}
                    </th>
                  );
                } else {
                  return (
                    <React.Fragment key={shutdown_type.id}></React.Fragment>
                  );
                }
              })}
            </tr>
            <tr className="w-max bg-gray-50">
              <td className="w-auto py-1 border border-gray-300 text-center text-sm text-gray-600 bg-gray-50">
                Actions
              </td>
              {outageTypes.map((shutdown_type) => {
                if (shutdown_type.display_type !== 0) {
                  return (
                    <td
                      className="w-auto py-1 border border-gray-300 text-center bg-gray-50"
                      key={`actions-${shutdown_type.id}`}
                    >
                      <div className="flex flex-col gap-1">
                        <button
                          className="text-xs bg-green-100 hover:bg-green-200 text-green-800 px-2 py-1 rounded"
                          onClick={() =>
                            handleColumnToggle(shutdown_type.id, true)
                          }
                        >
                          Select All
                        </button>
                        <button
                          className="text-xs bg-red-100 hover:bg-red-200 text-red-800 px-2 py-1 rounded"
                          onClick={() =>
                            handleColumnToggle(shutdown_type.id, false)
                          }
                        >
                          Deselect All
                        </button>
                      </div>
                    </td>
                  );
                } else {
                  return (
                    <React.Fragment
                      key={`actions-${shutdown_type.id}`}
                    ></React.Fragment>
                  );
                }
              })}
            </tr>
          </thead>
          <tbody>
            {regions.map(function (region) {
              // Find current selections for this region
              const currentRegionOutage = regionOutages.find(
                (outage) => outage.region_id === region.id,
              );
              const currentSelections =
                currentRegionOutage?.outages_types || EMPTY_ARRAY;

              return (
                <ShutdownLine
                  key={region.id}
                  region={region}
                  outages_types={outageTypes}
                  onCheckboxChanged={handleCheckboxChanged}
                  shutdownTypeColors={shutdownTypeColors}
                  existingShutdowns={existingShutdowns}
                  currentSelections={currentSelections}
                  userHasInteracted={userHasInteracted}
                />
              );
            })}
          </tbody>
          <tfoot>
            <tr className="h-8">
              <td
                colSpan={
                  outageTypes.filter((st) => st.display_type !== 0).length + 1
                }
                className="border-none border-inset"
              ></td>
            </tr>
            <tr className="bg-gray-100 font-semibold">
              <td className="w-auto py-2 border border-gray-300 text-center">
                Regions checked
              </td>
              {outageTypes.map((shutdown_type) => {
                if (shutdown_type.display_type !== 0) {
                  const checkedCount = regionOutages.filter((outage) =>
                    outage.outages_types.includes(shutdown_type.id),
                  ).length;

                  return (
                    <td
                      className="w-auto py-2 border border-gray-300 text-center"
                      key={`total-${shutdown_type.id}`}
                      style={{
                        backgroundColor:
                          checkedCount > 0
                            ? shutdownTypeColors[shutdown_type.id] || "#AACCAA"
                            : "transparent",
                        opacity: checkedCount > 0 ? 0.7 : 1,
                      }}
                    >
                      {checkedCount}
                    </td>
                  );
                } else {
                  return (
                    <React.Fragment
                      key={`total-${shutdown_type.id}`}
                    ></React.Fragment>
                  );
                }
              })}
            </tr>
            <tr className="bg-gray-200">
              <td className="w-auto py-2 border border-gray-300 text-center">
                Total
              </td>
              {(() => {
                const grandTotal = outageTypes
                  .filter((st) => st.display_type !== 0)
                  .reduce((sum, shutdown_type) => {
                    return (
                      sum +
                      regionOutages.filter((outage) =>
                        outage.outages_types.includes(shutdown_type.id),
                      ).length
                    );
                  }, 0);

                const visibleColumns = outageTypes.filter(
                  (st) => st.display_type !== 0,
                ).length;

                return (
                  <td
                    className="w-auto py-2 border border-gray-300 text-center"
                    colSpan={visibleColumns}
                  >
                    {grandTotal}
                  </td>
                );
              })()}
            </tr>
          </tfoot>
        </table>
      </div>
    </main>
  );
}

export default ShutdownAddPage;
